// AuthContext — expose session Supabase + méthodes signIn/signUp/signOut.
// Source : PRD 03 §3 Auth + Supabase JS onAuthStateChange pattern.
//
// Pattern : le Provider subscribe à auth.onAuthStateChange au mount et
// met à jour l'état user/session. Les hooks enfants `useAuth()` lisent
// ce state sans trigger un re-render superflu.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Session, User, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** captchaToken : token Cloudflare Turnstile. Required when CAPTCHA is
   *  enabled in Supabase Auth settings (which gates ALL flows, not just
   *  signup — Supabase enforces it on password sign-in too). */
  signIn: (
    email: string,
    password: string,
    captchaToken?: string,
  ) => Promise<{ error: AuthError | null }>;
  /** captchaToken : token Cloudflare Turnstile (Phase 9a anti-bot signup). */
  signUp: (email: string, password: string, captchaToken?: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  /** captchaToken : token Cloudflare Turnstile (Phase 9a anti-bot reset). */
  resetPassword: (email: string, captchaToken?: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Récupère la session initiale (depuis localStorage persisté)
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    // Subscribe aux changements (signin, signout, refresh token)
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setLoading(false);
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(
    async (email: string, password: string, captchaToken?: string) => {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        ...(captchaToken ? { options: { captchaToken } } : {}),
      });
      return { error };
    },
    [],
  );

  const signUp = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/verified`,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    return { error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const resetPassword = useCallback(async (email: string, captchaToken?: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset?verified=true`,
      ...(captchaToken ? { captchaToken } : {}),
    });
    return { error };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      session,
      loading,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      resetPassword,
    }),
    [session, loading, signIn, signUp, signInWithGoogle, signOut, resetPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
